import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/ui/Sidebar';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import ContentTypeBadge from '../../../components/ui/ContentTypeBadge';
import ArabicDateTime from '../components/ui/ArabicDateTime';
import ReviewNotesHistory from './components/ReviewNotesHistory';
import Button from '../../../components/ui/Button';
import Select from '../components/ui/Select';
import RichTextEditor from '../../../components/editor/RichTextEditor';
import adminService from '../../../services/adminService';
import articlesService from '../../../services/articlesService';
import mediaService from '../../../services/mediaService';
import circlesService from '../../../services/circlesService';

const EditorialQueueDashboard = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);

  // View / Edit mode toggle
  const [reviewMode, setReviewMode] = useState('view'); // 'view' | 'edit'
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState([{ sortOrder: 0, type: 'text', value: '' }]);
  const [editCoverImage, setEditCoverImage] = useState(null);
  const [editSelectedCircle, setEditSelectedCircle] = useState('');
  const [circles, setCircles] = useState([]);
  const [rootCircles, setRootCircles] = useState([]);
  const [poetrySubcategories, setPoetrySubcategories] = useState([]);
  const [circleDropdownOpen, setCircleDropdownOpen] = useState(false);
  const [poetryExpanded, setPoetryExpanded] = useState(false);
  const [coverImageUploading, setCoverImageUploading] = useState(false);
  const circleDropdownRef = useRef(null);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const titleTextareaRef = useRef(null);

  const fetchQueue = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await adminService.getQueue(params);
      setSubmissions(data.items || []);
    } catch (err) {
      console.error('Failed to fetch queue:', err);
      setError('فشل في تحميل قائمة الانتظار');
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQueue();
  }, [statusFilter]);

  useEffect(() => {
    circlesService.getCircles()
      .then(({ data }) => {
        const raw = data || [];
        setCircles(raw);
        const idToSlug = raw.reduce((acc, c) => { acc[c.id] = c.slug; return acc; }, {});
        const mapped = raw.map((c) => ({ ...c, parentSlug: c.parentId ? idToSlug[c.parentId] : null }));
        setRootCircles(mapped.filter((c) => !c.parentId));
        setPoetrySubcategories(mapped.filter((c) => c.parentSlug === 'poetry'));
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (circleDropdownRef.current && !circleDropdownRef.current.contains(e.target)) {
        setCircleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-resize title textarea to fit content (no scrollbar) - same as creator-studio
  useEffect(() => {
    const ta = titleTextareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.max(80, ta.scrollHeight)}px`;
  }, [editTitle, reviewMode]);

  const handleArticleClick = async (article) => {
    try {
      const { data } = await adminService.getQueuedArticle(article.id);
      setSelectedArticle(data);
    } catch {
      setSelectedArticle(article);
    }
    setAdminNotes('');
    setReviewMode('view');
  };

  const handleCloseReview = () => {
    setSelectedArticle(null);
    setAdminNotes('');
    setReviewMode('view');
  };

  const switchToEditMode = () => {
    if (!selectedArticle) return;
    setEditTitle(selectedArticle.title || '');
    setEditCoverImage(selectedArticle.coverImage || null);
    setEditSelectedCircle(selectedArticle.circleSlug || '');
    if (selectedArticle.content?.length) {
      const blocks = selectedArticle.content
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((b) => ({ sortOrder: b.sortOrder ?? 0, type: b.type, value: b.value || '' }));
      setEditContent(blocks);
    } else {
      setEditContent([{ sortOrder: 0, type: 'text', value: selectedArticle.excerpt || '' }]);
    }
    if (['classical-poetry', 'modern-poetry', 'colloquial-poetry'].includes(selectedArticle.circleSlug)) {
      setPoetryExpanded(true);
    }
    setReviewMode('edit');
  };

  const switchToViewMode = () => {
    setReviewMode('view');
  };

  const getFirstImageUrl = useCallback((blocks) => {
    const img = blocks?.find((c) => c.type === 'image' || c.type === 'Image');
    if (!img?.value) return null;
    try {
      const parsed = JSON.parse(img.value);
      return parsed?.url || img.value;
    } catch {
      return img.value;
    }
  }, []);

  const handleApprove = async () => {
    if (!selectedArticle) return;
    setActionLoading(true);
    try {
      if (reviewMode === 'edit') {
        const finalCover = editCoverImage || getFirstImageUrl(editContent) || null;
        const payload = {
          title: editTitle?.trim() || selectedArticle.title,
          content: editContent.map((c, idx) => ({ sortOrder: idx, type: c.type, value: c.value })),
          circleSlug: editSelectedCircle || selectedArticle.circleSlug,
          coverImageUrl: finalCover,
          type: selectedArticle.type || 'Article',
        };
        await articlesService.updateArticle(selectedArticle.id, payload);
      }
      await adminService.approveArticle(selectedArticle.id, adminNotes || null);
      handleCloseReview();
      fetchQueue();
    } catch (err) {
      console.error('Approve failed:', err);
      alert('فشل في الموافقة على المقال');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestEdit = async () => {
    if (!selectedArticle) return;
    if (!adminNotes.trim()) {
      alert('الرجاء كتابة ملاحظات للكاتب');
      return;
    }
    setActionLoading(true);
    try {
      await adminService.requestRevision(selectedArticle.id, adminNotes);
      handleCloseReview();
      fetchQueue();
    } catch (err) {
      console.error('Request revision failed:', err);
      alert('فشل في طلب التعديل');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!selectedArticle) return;
    setActionLoading(true);
    try {
      await adminService.rejectArticle(selectedArticle.id, adminNotes || null);
      handleCloseReview();
      fetchQueue();
    } catch (err) {
      console.error('Decline failed:', err);
      alert('فشل في رفض المقال');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'pending') return 'bg-warning';
    if (status === 'revision') return 'bg-accent';
    return 'bg-success';
  };

  const getStatusLabel = (status) => {
    if (status === 'pending') return 'معلق';
    if (status === 'revision') return 'مراجعة';
    return status;
  };

  const getSelectedCircleDisplayName = () => {
    if (!editSelectedCircle) return 'اختر الدائرة...';
    const fromRoot = rootCircles.find((c) => c.slug === editSelectedCircle);
    if (fromRoot) return fromRoot.name;
    const fromSub = poetrySubcategories.find((c) => c.slug === editSelectedCircle);
    return fromSub?.name || editSelectedCircle;
  };

  const uploadAndInsertImage = useCallback(async (file) => {
    try {
      const { data } = await mediaService.uploadImage(file);
      editorRef.current?.insertImage?.(data.url);
    } catch (err) {
      console.error(err);
      alert('فشل رفع الصورة');
    }
  }, []);

  const uploadAndInsertAudio = useCallback(async (file) => {
    try {
      const { data } = await mediaService.uploadAudio(file);
      editorRef.current?.insertAudio?.(data.url);
    } catch (err) {
      console.error(err);
      alert('فشل رفع الملف الصوتي');
    }
  }, []);

  const handleEditorImageFileSelect = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';
      await uploadAndInsertImage(file);
    },
    [uploadAndInsertImage]
  );

  const handleEditorAudioFileSelect = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = '';
      await uploadAndInsertAudio(file);
    },
    [uploadAndInsertAudio]
  );

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverImageUploading(true);
    try {
      const { data } = await mediaService.uploadImage(file);
      setEditCoverImage(data.url);
    } catch (err) {
      console.error(err);
      alert('فشل رفع صورة الغلاف');
    } finally {
      setCoverImageUploading(false);
    }
  };

  useEffect(() => {
    document.title = 'قائمة الانتظار - Dawayer';
  }, []);

  const getImageUrl = (block) => {
    if (block.type !== 'image') return null;
    try {
      const parsed = JSON.parse(block.value);
      return parsed?.url || block.value;
    } catch {
      return block.value;
    }
  };

  if (selectedArticle) {
    return (
      <div className="min-h-screen bg-[#F9F7F2]" dir="rtl">
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleEditorImageFileSelect} className="hidden" />
        <input ref={audioInputRef} type="file" accept="audio/*" onChange={handleEditorAudioFileSelect} className="hidden" />
        <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" />

        <Sidebar />
        <main className="lg:mr-60 transition-default">
          <div className="flex flex-col lg:flex-row h-screen">
            <div className="flex-1 overflow-y-auto p-6 lg:p-8">
              <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <button
                  onClick={() => navigate('/homepage')}
                  className="flex items-center gap-2 px-5 py-3 rounded-full font-medium text-[15px] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-[0_4px_14px_rgba(197,160,89,0.25)] hover:shadow-[0_6px_20px_rgba(197,160,89,0.35)] self-start order-1 sm:order-2"
                  style={{
                    fontFamily: 'Amiri, serif',
                    backgroundColor: '#C5A059',
                    color: '#FFFFFF'
                  }}
                >
                  <Icon name="Home" size={18} color="#FFFFFF" />
                  <span>الرجوع إلى المنصة</span>
                </button>
                <div className="flex items-center gap-3 order-2 sm:order-1">
                  <button
                    onClick={handleCloseReview}
                    className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-default font-body"
                  >
                    <Icon name="ArrowRight" size={20} />
                    <span>العودة إلى القائمة</span>
                  </button>
                  <div className="flex items-center gap-1 border border-border rounded-lg p-1 bg-card">
                    <button
                      onClick={switchToViewMode}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-default ${reviewMode === 'view' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                      style={{ fontFamily: 'Amiri, serif' }}
                    >
                      عرض
                    </button>
                    <button
                      onClick={switchToEditMode}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-default ${reviewMode === 'edit' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                      style={{ fontFamily: 'Amiri, serif' }}
                    >
                      تعديل
                    </button>
                  </div>
                </div>
              </div>

              {reviewMode === 'view' ? (
                <article className="max-w-3xl">
                  {selectedArticle?.coverImage && (
                    <Image
                      src={selectedArticle?.coverImage}
                      alt={selectedArticle?.coverImageAlt}
                      className="w-full h-64 lg:h-96 object-cover rounded-2xl mb-6"
                    />
                  )}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <ContentTypeBadge type={selectedArticle?.type} />
                    <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(selectedArticle?.status)}`}>
                      {getStatusLabel(selectedArticle?.status)}
                    </span>
                  </div>
                  <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-4" style={{ fontFamily: 'Amiri, serif', fontWeight: 700 }}>
                    {selectedArticle?.title}
                  </h1>
                  <div className="flex items-center gap-3 mb-6">
                    <button
                      type="button"
                      onClick={() => selectedArticle?.authorId && navigate(`/user/${selectedArticle.authorId}`)}
                      className="flex items-center gap-3 text-right hover:opacity-80 transition-opacity"
                    >
                      {selectedArticle?.authorAvatar && (
                        <Image
                          src={selectedArticle?.authorAvatar}
                          alt={selectedArticle?.author}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      )}
                      <div className="text-right">
                        <p className="font-body font-medium text-foreground hover:text-primary transition-colors">{selectedArticle?.author}</p>
                        {selectedArticle?.submittedAt && (
                          <ArabicDateTime date={new Date(selectedArticle?.submittedAt)} format="full" className="text-sm text-muted-foreground" />
                        )}
                      </div>
                    </button>
                  </div>

                  {selectedArticle?.content?.length > 0 ? (
                    <div className="prose prose-lg max-w-none text-right font-body text-foreground leading-relaxed space-y-4">
                      {selectedArticle.content.map((block, idx) => {
                        if (block.type === 'text') {
                          return <div key={idx} dangerouslySetInnerHTML={{ __html: block.value }} />;
                        }
                        if (block.type === 'image') {
                          const url = getImageUrl(block);
                          return url ? <img key={idx} src={url} alt="" className="w-full rounded-lg" /> : null;
                        }
                        if (block.type === 'audio') {
                          return <audio key={idx} controls src={block.value} className="w-full" />;
                        }
                        return null;
                      })}
                    </div>
                  ) : (
                    <div className="prose prose-lg max-w-none text-right font-body text-foreground leading-relaxed">
                      <p>{selectedArticle?.excerpt}</p>
                    </div>
                  )}
                </article>
              ) : (
                <div className="max-w-3xl">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <ContentTypeBadge type={selectedArticle?.type} />
                    <span className={`px-3 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(selectedArticle?.status)}`}>
                      {getStatusLabel(selectedArticle?.status)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-6">
                    <button
                      type="button"
                      onClick={() => selectedArticle?.authorId && navigate(`/user/${selectedArticle.authorId}`)}
                      className="flex items-center gap-3 text-right hover:opacity-80 transition-opacity"
                    >
                      {selectedArticle?.authorAvatar && (
                        <Image
                          src={selectedArticle?.authorAvatar}
                          alt={selectedArticle?.author}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      )}
                      <div className="text-right">
                        <p className="font-body font-medium text-foreground hover:text-primary transition-colors">{selectedArticle?.author}</p>
                        {selectedArticle?.submittedAt && (
                          <ArabicDateTime date={new Date(selectedArticle?.submittedAt)} format="full" className="text-sm text-muted-foreground" />
                        )}
                      </div>
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mb-6" ref={circleDropdownRef}>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setCircleDropdownOpen(!circleDropdownOpen);
                          if (circleDropdownOpen === false && ['classical-poetry', 'modern-poetry', 'colloquial-poetry'].includes(editSelectedCircle)) {
                            setPoetryExpanded(true);
                          }
                        }}
                        className="bg-transparent text-sm border border-gray-300 rounded-md px-3 py-1.5 min-w-[160px] text-right flex items-center justify-between gap-2 focus:outline-none hover:border-gray-400 transition-colors"
                        style={{ fontFamily: 'Amiri, serif' }}
                      >
                        <span>{getSelectedCircleDisplayName()}</span>
                        <Icon name={circleDropdownOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="flex-shrink-0" />
                      </button>
                      {circleDropdownOpen && (
                        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-[100] max-h-[320px] overflow-y-auto min-w-[200px]" dir="rtl">
                          {rootCircles.map((c) => (
                            <div key={c.id}>
                              {c.slug === 'poetry' ? (
                                <>
                                  <div className="flex items-center justify-between w-full px-4 py-2.5 hover:bg-gray-50">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setEditSelectedCircle(c.slug);
                                        setCircleDropdownOpen(false);
                                      }}
                                      className={`flex-1 text-right ${editSelectedCircle === c.slug ? 'text-[#C5A059] font-medium' : ''}`}
                                      style={{ fontFamily: 'Amiri, serif', fontSize: '14px' }}
                                    >
                                      {c.name}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setPoetryExpanded((prev) => !prev); }}
                                      className="p-1 hover:bg-gray-100 rounded"
                                    >
                                      <Icon name={poetryExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} />
                                    </button>
                                  </div>
                                  {poetryExpanded && (
                                    <div className="pr-4 border-r-2 border-[#C5A059]/30 mr-2">
                                      {poetrySubcategories.map((sub) => (
                                        <button
                                          key={sub.id}
                                          type="button"
                                          onClick={() => {
                                            setEditSelectedCircle(sub.slug);
                                            setCircleDropdownOpen(false);
                                          }}
                                          className={`w-full flex items-center gap-2 px-4 py-2 text-right hover:bg-gray-50 ${editSelectedCircle === sub.slug ? 'bg-[#C5A059]/10 text-[#C5A059] font-medium' : 'text-gray-700'}`}
                                          style={{ fontFamily: 'Amiri, serif', fontSize: '13px' }}
                                        >
                                          {sub.name}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditSelectedCircle(c.slug);
                                    setCircleDropdownOpen(false);
                                  }}
                                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-right hover:bg-gray-50 ${editSelectedCircle === c.slug ? 'bg-[#C5A059]/10 text-[#C5A059]' : ''}`}
                                  style={{ fontFamily: 'Amiri, serif', fontSize: '14px' }}
                                >
                                  {c.name}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => coverInputRef.current?.click()}
                      className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors text-sm"
                      title="تحميل صورة الغلاف"
                    >
                      <Icon name="Image" size={16} />
                      <span style={{ fontFamily: 'Amiri, serif' }}>
                        {editCoverImage ? 'تغيير الغلاف' : 'صورة الغلاف'}
                      </span>
                    </button>
                    {coverImageUploading && <span className="text-xs text-gray-500">جاري الرفع...</span>}
                  </div>

                  {editCoverImage && (
                    <div className="mb-8 relative w-full h-[300px] rounded-xl overflow-hidden shadow-md group">
                      <img src={editCoverImage} alt="Cover" className="w-full h-full object-cover" />
                      <button
                        onClick={() => setEditCoverImage(null)}
                        className="absolute top-2 right-2 bg-white/80 p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Icon name="X" size={16} />
                      </button>
                    </div>
                  )}

                  <div className="mt-6">
                    <RichTextEditor
                      ref={editorRef}
                      contentBlocks={editContent}
                      onChange={(blocks) => setEditContent(blocks)}
                      onRequestInsertImage={(file) => (file ? uploadAndInsertImage(file) : fileInputRef.current?.click())}
                      onRequestInsertAudio={(file) => (file ? uploadAndInsertAudio(file) : audioInputRef.current?.click())}
                      placeholder="احكِ قصتك..."
                      titleSlot={
                        <textarea
                          ref={titleTextareaRef}
                          value={editTitle}
                          onChange={(e) => setEditTitle(e?.target?.value)}
                          placeholder="العنوان"
                          rows={1}
                          className="w-full min-h-[80px] px-0 py-4 bg-transparent resize-none focus:outline-none mt-4 overflow-hidden"
                          style={{ fontFamily: 'Amiri, serif', fontWeight: 700, fontSize: '48px', lineHeight: '1.2', color: '#2C2B29', direction: 'rtl', border: 'none', boxShadow: 'none' }}
                        />
                      }
                    />
                  </div>

                  </div>
              )}
            </div>

            <aside className="lg:w-96 bg-card border-r border-border p-6 lg:p-8 overflow-y-auto">
              <h2 className="text-xl font-heading font-bold text-foreground mb-6">لوحة القرار</h2>

              <ReviewNotesHistory items={selectedArticle?.reviewNotesHistory ?? []} />

              <div className="mb-6">
                <label className="block text-sm font-body font-medium text-foreground mb-2">
                  ملاحظات الإدارة
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e?.target?.value)}
                  placeholder="أضف ملاحظاتك هنا..."
                  className="w-full h-32 px-4 py-3 rounded-lg border border-border bg-background text-foreground font-body resize-none focus:outline-none focus:ring-2 focus:ring-primary transition-default"
                  dir="rtl"
                />
              </div>

              <div className="space-y-3">
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="w-full bg-success hover:bg-success/90 text-white"
                  iconName="Check"
                  iconPosition="right"
                >
                  {actionLoading ? 'جاري...' : 'نشر'}
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={handleRequestEdit}
                  disabled={actionLoading}
                  className="w-full"
                  iconName="Edit"
                  iconPosition="right"
                >
                  طلب تعديل
                </Button>
                <button
                  onClick={handleDecline}
                  disabled={actionLoading}
                  className="w-full py-3 text-center text-muted-foreground hover:text-error font-body font-medium transition-default disabled:opacity-50"
                >
                  رفض
                </button>
              </div>
            </aside>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]" dir="rtl">
      <Sidebar />
      <main className="lg:mr-60 transition-default">
        <div className="p-6 lg:p-8">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="sm:order-1">
              <h1 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                قائمة الانتظار
              </h1>
              <p className="text-base font-body text-muted-foreground">
                مراجعة المقالات المعلقة
              </p>
            </div>
            <button
              onClick={() => navigate('/homepage')}
              className="flex items-center gap-2 px-5 py-3 rounded-full font-medium text-[15px] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-[0_4px_14px_rgba(197,160,89,0.25)] hover:shadow-[0_6px_20px_rgba(197,160,89,0.35)] self-start sm:order-2"
              style={{
                fontFamily: 'Amiri, serif',
                backgroundColor: '#C5A059',
                color: '#FFFFFF'
              }}
            >
              <Icon name="Home" size={18} color="#FFFFFF" />
              <span>الرجوع إلى المنصة</span>
            </button>
          </div>

          <div className="mb-6 w-fit" dir="rtl">
            <Select
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              options={[
                { value: 'all', label: 'الكل' },
                { value: 'pending', label: 'معلق' },
                { value: 'revision', label: 'مراجعة' }
              ]}
              placeholder="الكل"
              className="min-w-[160px]"
            />
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-error" />
              <p className="text-muted-foreground font-body">{error}</p>
              <button onClick={fetchQueue} className="mt-4 text-primary hover:underline font-body">
                إعادة المحاولة
              </button>
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="CheckCircle" size={48} className="mx-auto mb-4 text-success" />
              <p className="text-lg font-body text-muted-foreground">
                لا توجد مقالات معلقة للمراجعة
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {submissions.map((submission) => (
                <div
                  key={submission?.id}
                  onClick={() => handleArticleClick(submission)}
                  className="flex items-center gap-4 p-4 lg:p-6 rounded-2xl bg-card hover:shadow-warm-md transition-default cursor-pointer"
                >
                  <div className={`w-3 h-3 rounded-full ${getStatusColor(submission?.status)} flex-shrink-0`} />
                  {submission?.authorAvatar ? (
                    <Image
                      src={submission?.authorAvatar}
                      alt={submission?.author}
                      className="w-12 h-12 lg:w-14 lg:h-14 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={24} color="var(--color-primary)" />
                    </div>
                  )}
                  <div className="flex-1 text-right min-w-0">
                    <div className="flex items-center gap-2 mb-1 min-w-0">
                      <ContentTypeBadge type={submission?.type} size="sm" className="flex-shrink-0" />
                      <h3 className="text-base lg:text-lg font-semibold text-foreground truncate min-w-0" style={{ fontFamily: 'Amiri, serif', fontWeight: 700 }}>
                        {submission?.title}
                      </h3>
                    </div>
                    {submission?.authorId ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/user/${submission.authorId}`);
                        }}
                        className="text-sm font-body text-muted-foreground hover:text-primary transition-colors"
                      >
                        {submission?.author}
                      </button>
                    ) : (
                      <p className="text-sm font-body text-muted-foreground">{submission?.author}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(submission?.status)}`}>
                      {getStatusLabel(submission?.status)}
                    </span>
                    {submission?.submittedAt && (
                      <ArabicDateTime
                        date={new Date(submission?.submittedAt)}
                        format="relative"
                        className="text-sm text-muted-foreground"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default EditorialQueueDashboard;
